import { AccountsForSelectionGQL } from '../../graphql/gql';
import { CommonModule } from '@angular/common';
import { Component, forwardRef, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { TreeSelectModule } from 'primeng/treeselect';

interface AccountOption {
  clientNumber: number;
  company: string;
}

interface TreeNode {
  key: string;
  label: string;
  data: AccountOption;
  children?: TreeNode[];
}

interface AccountNode {
  clientNumber: number;
  company: string;
  parent?: {
    clientNumber: number;
    company: string;
  };
}

// The AccountSelectorComponent uses PrimeNG's TreeSelect to allow users to select accounts.
@Component({
  selector: 'app-account-selector',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TreeSelectModule
  ],
  templateUrl: './account-selector.component.html',
  styleUrl: './account-selector.component.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AccountSelectorComponent),
      multi: true
    }
  ]
})
export class AccountSelectorComponent implements ControlValueAccessor, OnChanges {
  @Input() selectionHeader: string = $localize`Available accounts`;

  // An account which is not part of the `AccountsForSelection` result set but must still be
  // selectable, e.g. the current user's own account: that query only returns its children.
  // Merging it in also re-roots those children underneath it.
  @Input() additionalAccount?: AccountOption;

  accounts: TreeNode[] = [];
  selectedAccount: TreeNode | undefined = undefined;
  loading = false;
  disabled = false;
  private accountsLoaded = false;
  private fetchedAccounts: AccountNode[] = [];
  private pendingClientNumber: number | null = null;

  onChanged: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  constructor(
    private accountsForSelectionGQL: AccountsForSelectionGQL
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['additionalAccount']) {
      return;
    }

    // `additionalAccount` is bound during change detection, i.e. after `writeValue` has already
    // run for a value set programmatically. Rebuild the tree and retry the pending lookup.
    this.accounts = this.buildTree(this.mergedAccounts());
    this.resolvePendingSelection();
  }

  writeValue(value: number | null | undefined): void {
    if (value === null || value === undefined) {
      this.pendingClientNumber = null;
      this.selectedAccount = undefined;
      return;
    }

    this.pendingClientNumber = value;
    this.selectedAccount = this.findNodeByClientNumber(this.accounts, value);

    if (!this.selectedAccount && !this.accountsLoaded && !this.loading) {
      this.fetchAccounts();
    }
  }

  registerOnChange(fn: (value: any) => void) {
    this.onChanged = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  onSelect(event: any) {
    const node = event.node as TreeNode;
    this.selectedAccount = node;
    this.pendingClientNumber = node?.data?.clientNumber ?? null;
    this.onChanged(node?.data?.clientNumber ?? null); // Emit only clientNumber
    this.onTouched();
  }

  onClear() {
    this.selectedAccount = undefined;
    this.pendingClientNumber = null;
    this.onChanged(null);
    this.onTouched();
  }

  onShow() {
    if (!this.accountsLoaded) {
      this.fetchAccounts();
    }
  }

  handleBlur() {
    this.onTouched();
  }

  private fetchAccounts() {
    this.loading = true;

    this.accountsForSelectionGQL.fetch({ states: ['active'] }).subscribe({
      next: (result) => {
        this.fetchedAccounts = result.data.accounts.edges?.map((edge: any) => edge.node) ?? [];

        this.accounts = this.buildTree(this.mergedAccounts());
        this.accountsLoaded = true;
        this.loading = false;

        this.resolvePendingSelection();
      },
      error: () => {
        this.fetchedAccounts = [];
        this.accountsLoaded = false;
        this.loading = false;

        // `additionalAccount` does not come from the network, so a preset value can still resolve.
        this.accounts = this.buildTree(this.mergedAccounts());
        this.resolvePendingSelection();
      }
    });
  }

  // `writeValue` may run before the accounts are available; resolve the node once they are.
  private resolvePendingSelection() {
    if (this.pendingClientNumber === null) {
      return;
    }

    this.selectedAccount = this.findNodeByClientNumber(this.accounts, this.pendingClientNumber);
  }

  private mergedAccounts(): AccountNode[] {
    if (!this.additionalAccount) {
      return this.fetchedAccounts;
    }

    const alreadyPresent = this.fetchedAccounts.some(
      account => account.clientNumber === this.additionalAccount!.clientNumber
    );

    return alreadyPresent ? this.fetchedAccounts : [this.additionalAccount, ...this.fetchedAccounts];
  }

  // Translated the flat list of accounts with parents into a tree structure
  // with a children array for TreeSelect.
  private buildTree(accounts: AccountNode[]): TreeNode[] {
    // Create a map for quick lookup by clientNumber
    const accountMap = new Map<number, TreeNode>();
    const rootNodes: TreeNode[] = [];

    // First pass: Create TreeNode for each account
    accounts.forEach(account => {
      const treeNode: TreeNode = {
        key: account.clientNumber.toString(),
        label: `${account.clientNumber} - ${account.company}`,
        data: {
          clientNumber: account.clientNumber,
          company: account.company
        },
        children: []
      };
      accountMap.set(account.clientNumber, treeNode);
    });

    // Second pass: Build parent-child relationships
    accounts.forEach(account => {
      const currentNode = accountMap.get(account.clientNumber)!;

      if (account.parent && account.parent.clientNumber) {
        // This account has a parent
        const parentNode = accountMap.get(account.parent.clientNumber);
        if (parentNode) {
          // Parent exists in our dataset, add as child
          if (!parentNode.children) {
            parentNode.children = [];
          }
          parentNode.children.push(currentNode);
        } else {
          // Parent not in dataset, treat as root
          rootNodes.push(currentNode);
        }
      } else {
        // No parent, it's a root node
        rootNodes.push(currentNode);
      }
    });

    // Sort function for TreeNodes by clientNumber
    const sortByClientNumber = (a: TreeNode, b: TreeNode) =>
      a.data.clientNumber - b.data.clientNumber;

    // Recursively sort children
    const sortChildren = (node: TreeNode) => {
      if (node.children && node.children.length > 0) {
        node.children.sort(sortByClientNumber);
        node.children.forEach(sortChildren);
      }
    };

    // Clean up empty children arrays and sort
    accountMap.forEach(node => {
      if (node.children && node.children.length === 0) {
        delete node.children;
      } else if (node.children) {
        sortChildren(node);
      }
    });

    // Sort root nodes
    rootNodes.sort(sortByClientNumber);

    return rootNodes;
  }

  private findNodeByClientNumber(nodes: TreeNode[], clientNumber: number): TreeNode | undefined {
    for (const node of nodes) {
      if (node.data.clientNumber === clientNumber) {
        return node;
      }
      if (node.children) {
        const found = this.findNodeByClientNumber(node.children, clientNumber);
        if (found) return found;
      }
    }
    return undefined;
  }
}
